import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import PrivacyTipRoundedIcon from "@mui/icons-material/PrivacyTipRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import { ConsentFormData, ConsentOption } from "../types";
import { useUpdateConsent } from "../mutations";
import { TERMS_OF_SERVICE_AND_PRIVACY_NOTICE } from "../constants/termsOfService";
import { TermsOfServiceDialog } from "./TermsOfServiceDialog";

interface ConsentPopupProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ConsentFormData) => void;
}

export const ConsentPopup: React.FC<ConsentPopupProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const [selection, setSelection] = useState<ConsentOption>("consent");
  const [privacyPolicyAcknowledged, setPrivacyPolicyAcknowledged] =
    useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const [error, setError] = useState("");
  const [showTermsDialog, setShowTermsDialog] = useState(false);

  const updateConsentMutation = useUpdateConsent();

  const isValid = useMemo(() => {
    if (selection === "consent") {
      return privacyPolicyAcknowledged && confirmationText.trim().length > 0;
    }

    return (
      confirmationText.trim().toLowerCase() === "i do not consent".toLowerCase()
    );
  }, [confirmationText, privacyPolicyAcknowledged, selection]);

  const handleSubmit = async () => {
    if (!isValid) {
      return;
    }

    setError("");

    try {
      await updateConsentMutation.mutateAsync({
        privacyPolicyAcknowledged,
        decision: selection,
        confirmationText: confirmationText.trim(),
      });

      onSubmit({
        option: selection,
        privacyPolicyAcknowledged,
        confirmationText,
      });
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Failed to update consent"
      );
    }
  };

  const handleClose = () => {
    setSelection("consent");
    setPrivacyPolicyAcknowledged(false);
    setConfirmationText("");
    setError("");
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: 2,
              bgcolor: "warning.main",
              color: "warning.contrastText",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              shrink: 0,
              flexShrink: 0,
            }}
          >
            <PrivacyTipRoundedIcon fontSize="large" />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="h2" fontWeight={600}>
              We need your permission to get started
            </Typography>
            <Typography variant="body2" color="text.secondary">
              To participate in the PEAK4 programme we require consent to
              collect and monitor your wellbeing data.
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 2,
              maxHeight: 260,
              overflowY: "auto",
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" mb={1}>
              Terms of Service and Privacy Notice
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
              {TERMS_OF_SERVICE_AND_PRIVACY_NOTICE}
            </Typography>
            <Button
              sx={{ mt: 1.5 }}
              variant="text"
              size="small"
              onClick={() => setShowTermsDialog(true)}
            >
              Open full document
            </Button>
          </Box>

          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ mb: 1.5 }}>
              How would you like us to handle your wellbeing data?
            </FormLabel>
            <RadioGroup
              value={selection}
              onChange={(event) =>
                setSelection(event.target.value as ConsentOption)
              }
            >
              <FormControlLabel
                value="consent"
                sx={{ alignItems: "flex-start", mb: 3 }}
                control={<Radio />}
                label={
                  <Typography variant="body2">
                    I explicitly consent to PEAK4 collecting and monitoring the
                    above wellbeing data for the purposes of the PEAK4 programme
                  </Typography>
                }
              />
              {selection === "consent" && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={privacyPolicyAcknowledged}
                      onChange={(event) =>
                        setPrivacyPolicyAcknowledged(event.target.checked)
                      }
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I confirm that I have read the Terms of Service and
                      Privacy Notice for the PEAK4 programme
                    </Typography>
                  }
                  sx={{ mb: 3 }}
                />
              )}
              <FormControlLabel
                value="decline"
                sx={{ alignItems: "flex-start", mb: 3 }}
                control={<Radio />}
                label={
                  <Typography variant="body2">
                    I DO NOT consent to PEAK4 collecting and monitoring the
                    above wellbeing data for the purposes of the PEAK4 programme
                    only please select this option.
                  </Typography>
                }
              />
            </RadioGroup>
          </FormControl>

          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircleOutlineRoundedIcon color="success" fontSize="small" />
              <Typography variant="subtitle2" color="text.secondary">
                Confirm your decision
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              You can withdraw consent at any time by emailing
              dataprivacy@peak4.co.uk. Please provide your full name as written
              confirmation. If you do not consent, type "I DO NOT CONSENT".
            </Typography>
            <TextField
              fullWidth
              label={
                selection === "consent"
                  ? "Full name"
                  : "Type 'I DO NOT CONSENT'"
              }
              value={confirmationText}
              onChange={(event) => setConfirmationText(event.target.value)}
              required
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button variant="outlined" onClick={handleClose} sx={{ minWidth: 100 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid || updateConsentMutation.isPending}
          sx={{ minWidth: 120 }}
        >
          {updateConsentMutation.isPending
            ? "Saving..."
            : selection === "consent"
            ? "Proceed"
            : "Confirm Decline"}
        </Button>
      </DialogActions>
      </Dialog>
      <TermsOfServiceDialog
        open={showTermsDialog}
        onClose={() => setShowTermsDialog(false)}
      />
    </>
  );
};
